import { useState, useEffect } from "react";
import { Loader2, CalendarCheck, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { format, addDays } from "date-fns";
import { it } from "date-fns/locale";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { FadeUp, SectionLabel } from "@/components/Reveal";
import { api, formatApiError, useContent } from "@/lib/api";

const SLOTS = ["09:00", "10:00", "11:00", "12:00", "14:30", "15:30", "16:30", "17:30", "18:30"];

export default function BookCall() {
  const content = useContent();
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", notes: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [blockedDays, setBlockedDays] = useState(new Set());
  const [takenSlots, setTakenSlots] = useState(new Set());

  const days = Array.from({ length: 21 }, (_, i) => addDays(new Date(), i + 1)).filter((d) => d.getDay() !== 0);

  useEffect(() => {
    api.get("/bookings/availability").then((r) => {
      const daysSet = new Set();
      const slotsSet = new Set();
      for (const b of r.data.blocked || []) {
        if (b.time) slotsSet.add(`${b.date}|${b.time}`);
        else daysSet.add(b.date);
      }
      for (const b of r.data.booked || []) {
        slotsSet.add(`${b.date}|${b.time}`);
      }
      setBlockedDays(daysSet);
      setTakenSlots(slotsSet);
    }).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/bookings", {
        ...form,
        date: format(date, "yyyy-MM-dd"),
        time,
      });
      setDone(true);
      toast.success("Richiesta inviata. Ti confermerò la call via email.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="book-call-page">
      <Nav />
      <section className="mx-auto max-w-5xl px-6 pb-28 pt-40 lg:pt-52">
        <SectionLabel>Call conoscitiva gratuita</SectionLabel>
        <FadeUp as="h1" delay={0.1} className="mt-6 max-w-3xl font-display text-5xl leading-[1.05] text-ivory md:text-6xl" data-testid="book-call-title">
          Prenota la tua <span className="italic text-mist">call.</span>
        </FadeUp>
        <FadeUp delay={0.2} className="mt-8 max-w-xl text-sm leading-loose text-mute md:text-base">
          20 minuti per conoscerci: mi racconti i tuoi obiettivi e la tua routine, e capiamo insieme
          quale percorso fa per te. Scegli giorno e orario, ti confermo via email.
        </FadeUp>

        {done ? (
          <FadeUp className="mt-16 flex flex-col items-start gap-6 border border-sage bg-surface p-12" data-testid="booking-success">
            <CalendarCheck size={40} className="text-sage" />
            <p className="font-display text-3xl text-ivory">Richiesta inviata.</p>
            <p className="max-w-md text-sm leading-relaxed text-mute">
              Ho registrato la tua richiesta per {format(date, "EEEE d MMMM", { locale: it })} alle {time}.
              Riceverai una email di conferma con il link per la videocall.
            </p>
            <Link to="/" data-testid="booking-back-home" className="btn-outline">Torna alla home</Link>
          </FadeUp>
        ) : (
          <form onSubmit={submit} className="mt-16 grid gap-12 lg:grid-cols-2" data-testid="booking-form">
            <div>
              <p className="label-caps mb-6 text-ivory/60">1. Scegli il giorno</p>
              <div className="grid max-h-72 grid-cols-3 gap-2 overflow-y-auto pr-2 sm:grid-cols-4" data-testid="booking-days">
                {days.map((d) => {
                  const key = format(d, "yyyy-MM-dd");
                  const selected = date && format(date, "yyyy-MM-dd") === key;
                  const isBlocked = blockedDays.has(key);
                  return (
                    <button
                      type="button"
                      key={key}
                      onClick={() => { if (!isBlocked) { setDate(d); setTime(null); } }}
                      disabled={isBlocked}
                      data-testid={`booking-day-${key}`}
                      className={`border px-2 py-3 text-center transition-colors duration-300 ${
                        isBlocked
                          ? "cursor-not-allowed border-line/50 text-mute/40 line-through"
                          : selected
                            ? "border-sage bg-sage text-ivory"
                            : "border-line text-ivory/80 hover:border-mist/50"
                      }`}
                    >
                      <span className="block text-[10px] uppercase tracking-widest opacity-70">{format(d, "EEE", { locale: it })}</span>
                      <span className="block font-display text-lg">{format(d, "d MMM", { locale: it })}</span>
                    </button>
                  );
                })}
              </div>
              <p className="label-caps mb-6 mt-10 text-ivory/60">2. Scegli l'orario</p>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4" data-testid="booking-slots">
                {SLOTS.map((s) => {
                  const isTaken = date && takenSlots.has(`${format(date, "yyyy-MM-dd")}|${s}`);
                  return (
                    <button
                      type="button"
                      key={s}
                      onClick={() => { if (!isTaken) setTime(s); }}
                      disabled={isTaken}
                      data-testid={`booking-slot-${s.replace(":", "-")}`}
                      className={`border px-2 py-3 text-sm transition-colors duration-300 ${
                        isTaken
                          ? "cursor-not-allowed border-line/50 text-mute/40 line-through"
                          : time === s
                            ? "border-sage bg-sage text-ivory"
                            : "border-line text-ivory/80 hover:border-mist/50"
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex flex-col gap-5">
              <p className="label-caps text-ivory/60">3. I tuoi dati</p>
              <div>
                <label className="label-caps mb-2 block text-ivory/60" htmlFor="bk-name">Nome e cognome</label>
                <input id="bk-name" required minLength={2} className="input-dark" data-testid="booking-name"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="label-caps mb-2 block text-ivory/60" htmlFor="bk-email">Email</label>
                <input id="bk-email" type="email" required className="input-dark" data-testid="booking-email"
                  value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <label className="label-caps mb-2 block text-ivory/60" htmlFor="bk-phone">Telefono / WhatsApp</label>
                <input id="bk-phone" className="input-dark" data-testid="booking-phone"
                  value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <label className="label-caps mb-2 block text-ivory/60" htmlFor="bk-notes">Note (opzionale)</label>
                <textarea id="bk-notes" rows={3} className="input-dark resize-none" data-testid="booking-notes"
                  value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Obiettivi, disponibilità particolari..." />
              </div>
              <button type="submit" disabled={sending || !date || !time} data-testid="booking-submit" className="btn-primary mt-2 w-fit disabled:opacity-50">
                {sending ? <Loader2 size={14} className="animate-spin" /> : "Conferma prenotazione"}
              </button>
              {!date || !time ? <p className="text-xs text-mute">Seleziona giorno e orario per continuare.</p> : null}
            </div>
          </form>
        )}
        <FadeUp delay={0.1} className="mt-16">
          <Link to="/contatti" data-testid="booking-contact-link" className="label-caps flex w-fit items-center gap-2 text-mute transition-colors duration-300 hover:text-ivory">
            <ArrowLeft size={14} /> Preferisci scrivermi? Vai ai contatti
          </Link>
        </FadeUp>
      </section>
      <Footer content={content} />
    </div>
  );
}
